import { useCallback, useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useToast } from '../../../components/ui/ToastProvider'
import type { EditOp } from '../../../lib/editOp'
import { reorderMeetingAgenda } from '../api/meetingApi'
import { useSyncMeetingItems } from './useSyncMeetingItems'
import {
  buildMinuteItemsPayload,
  hasDescription,
  itemToRow,
  type MinuteItem,
  type MinuteItemKind,
  type MinuteItemRow,
} from '../../minuteItems/types'

/**
 * Toplantı Detay'da basit içerik (Gündem/Prensip/Karar) düzenlemesi — hep düzenlenebilir, her ekle/düzenle/sil
 * <b>anında</b> senkronlanır (bağımsız uç). İyimser güncelleme; sunucu hatası veya eksik-alanlı satır →
 * son kaydedilen duruma geri döner. Aksiyon instant-save mantığının basit içerik karşılığı ({@code
 * MeetingDetailPage} aksiyon deseniyle birebir). {@link init} toplantı yüklenince satırları detaydan başlatır.
 */
export function useEditableMinuteItems(meetingId: number, kind: MinuteItemKind) {
  const { t } = useTranslation()
  const toast = useToast()
  const sync = useSyncMeetingItems(meetingId, kind)
  const withDescription = hasDescription(kind)

  const [rows, setRows] = useState<MinuteItemRow[]>([])
  const committedRef = useRef<MinuteItemRow[]>([]) // son başarılı kaydedilen satırlar (hata → geri al)
  // Her sync isteğine artan sıra no. Hızlı ekle/sil'de birden çok istek in-flight olur; yanıtlar SIRA DIŞI
  // gelebilir. Yalnız EN SON isteğin yanıtı uygulanır; geç gelen eski yanıt (silinen satırı geri getirir)
  // yut → silinen satırın "ekranda kalıp refresh isteme" yarışı önlenir. Payload hep tüm satırları taşır.
  const syncSeqRef = useRef(0)

  const init = useCallback((items: MinuteItem[]) => {
    const fresh = items.map(itemToRow)
    setRows(fresh)
    committedRef.current = fresh
  }, [])

  const reorderMut = useMutation({
    mutationFn: (orderedIds: number[]) => reorderMeetingAgenda(meetingId, orderedIds),
  })

  /**
   * Sürükle-bırak sonrası yeni sırayı kaydeder (yalnız Gündem; parent onReorder'ı yalnız agenda editörüne verir).
   * İyimser: satırları hemen yeni sıraya alır; sunucu record_number'ı 1..N yazıp güncel listeyi döndürünce
   * "No" tazelenir. Hata → son kaydedilene döner. Kaydedilmemiş (id'siz) satır varsa sıralama atlanır.
   */
  const reorder = (orderedRows: MinuteItemRow[]) => {
    const ids = orderedRows.map((r) => r.id).filter((x): x is number => x != null)
    if (ids.length !== orderedRows.length) {
      return // yeni/kaydedilmemiş satır var → önce senkron beklenir
    }
    setRows(orderedRows) // iyimser
    const mySeq = ++syncSeqRef.current
    reorderMut.mutate(ids, {
      onSuccess: (updated) => {
        if (mySeq !== syncSeqRef.current) return
        const fresh = updated.map(itemToRow)
        committedRef.current = fresh
        setRows(fresh)
        toast.success(t('meetings.detail.saved.reorder'))
      },
      onError: () => {
        if (mySeq !== syncSeqRef.current) return
        setRows(committedRef.current)
        toast.error(t('meetings.detail.itemsSaveError'))
      },
    })
  }

  const onChange = (next: MinuteItemRow[], op: EditOp) => {
    setRows(next) // iyimser
    const payload = buildMinuteItemsPayload(next, withDescription)
    if (payload === 'invalid') {
      setRows(committedRef.current)
      toast.error(t('minuteItems.common.invalid'))
      return
    }
    const mySeq = ++syncSeqRef.current
    sync.mutate(payload, {
      onSuccess: (updated) => {
        if (mySeq !== syncSeqRef.current) return // daha yeni istek uçtu → bayat yanıtı uygulama
        const fresh = updated.map(itemToRow)
        committedRef.current = fresh
        setRows(fresh)
        toast.success(t(`meetings.detail.saved.${op}`, { item: t(`meetings.detail.saved.item.${kind}`) }))
      },
      onError: () => {
        if (mySeq !== syncSeqRef.current) return // daha yeni istek var → onun sonucu belirleyici
        setRows(committedRef.current) // sunucu gerçeğine geri dön
        toast.error(t('meetings.detail.itemsSaveError'))
      },
    })
  }

  return { rows, onChange, init, reorder }
}
